import { AlertCircle, Mail, ShieldCheck, X } from "lucide-react";
import { useRef, useState } from "react";
import { StepIndicator } from "./StepIndicator";

import axios from "axios";
import toast from "react-hot-toast";
import { BASE_URL } from "../../../../config/api";


export function VerifyModal({ onClose, onVerified, selectedRowId }) {
  const STEPS = ["Invoice", "Send OTP", "Verify OTP"];
  const [step, setStep]               = useState("invoice");
  const [invoiceVal, setInvoiceVal]   = useState("");
  const [invoiceError, setInvoiceError] = useState("");
  const [isSending, setIsSending]     = useState(false);
  const [otpDigits, setOtpDigits]     = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError]       = useState("");
  const [redirectPct, setRedirectPct] = useState(0);
  const [redirectSecs, setRedirectSecs] = useState(3);

  const [requestId, setRequestId] = useState(null);

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
  const stepIndex = { invoice: 0, sendOtp: 1, verifyOtp: 2, success: 3 };

  const handleInvoiceNext = () => {
    if (!invoiceVal.trim() || invoiceVal.trim().length < 5) {
      setInvoiceError("Please enter a valid invoice number (e.g. INV-9001-2025)");
      return;
    }
    setInvoiceError("");
    setStep("sendOtp");
  };

 const handleSendOtp = async () => {
  try {
    setIsSending(true);

    const payload = {
      payout_id: selectedRowId, // ⚠️ pass this as prop
      entity_id: authUser?.id,
      role: authUser?.role,
      invoice_number: invoiceVal,
      remark: "", // add state if needed
      email: authUser?.contact_email,
    };

    const res = await axios.post(
      `${BASE_URL}warranty/payout-request`,
      payload
    );

    if (!res.data?.status) {
      toast.error(res.data?.message || "Failed to send OTP");
      return;
    }

    // ✅ save request_id
    setRequestId(res.data.request_id);

    toast.success("OTP sent successfully");

    setStep("verifyOtp");
    setTimeout(() => otpRefs[0].current?.focus(), 100);

  } catch (err) {
    console.error(err);
    toast.error("Something went wrong while sending OTP");
  } finally {
    setIsSending(false);
  }
};

  const handleOtpInput = (index, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otpDigits];
    next[index] = val.slice(-1);
    setOtpDigits(next);
    setOtpError("");
    if (val && index < 5) otpRefs[index + 1].current?.focus();
  };

  const handleOtpKeydown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyOtp = async () => {
  try {
    const otp = otpDigits.join("");

    if (otp.length !== 6) {
      setOtpError("Please enter valid 6-digit OTP");
      return;
    }

    const res = await axios.post(
      `${BASE_URL}warranty/payout-verify-otp`,
      {
        request_id: requestId,
        otp,
      }
    );

    if (!res.data?.status) {
      setOtpError(res.data?.message || "Invalid OTP");
      return;
    }

    toast.success("OTP verified successfully");

    setStep("success");

    // 🔥 redirect after success
    setTimeout(() => {
      onClose();
      onVerified("billed"); // 👈 pass tab info
    }, 1500);

  } catch (err) {
    console.error(err);
    setOtpError("Verification failed");
  }
};

  const resendOtp = () => {
    setOtpDigits(["", "", "", "", "", ""]);
    setOtpError("");
    setTimeout(() => otpRefs[0].current?.focus(), 50);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden"
        style={{ animation: "slideUp 0.25s ease" }}
      >

        {/* ── Invoice Step ── */}
        {step === "invoice" && (
          <div className="p-7">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-[15px] font-black text-slate-900">Verify Payout Summary</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">This month's commission · Week 42–43 · 2025</p>
              </div>
              <button onClick={onClose} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
                <X size={13} />
              </button>
            </div>

            <StepIndicator steps={STEPS} current={0} />

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-[10px] space-y-1.5">
              {[
                ["Period",        "Week 42–43 · Oct–Nov 2025"],
                ["Total Payouts", "5 records"],
                ["Total Amount",  "₹75,500"],
                ["Transferred",   "₹37,000 (2 payouts)"],
                ["Pending",       "₹38,500 (3 payouts)"],
              ].map(([lbl, val]) => (
                <div key={lbl} className="flex justify-between">
                  <span className="text-slate-500">{lbl}</span>
                  <span className="font-bold text-slate-800">{val}</span>
                </div>
              ))}
            </div>

            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
              Enter Invoice Number
            </label>
            <input
              type="text"
              value={invoiceVal}
              onChange={(e) => { setInvoiceVal(e.target.value); setInvoiceError(""); }}
              placeholder="e.g. INV-9001-2025"
              className="w-full border rounded-xl px-3 py-2.5 text-[12px] font-mono text-slate-800 outline-none transition-all"
              style={{
                borderColor: invoiceError ? "#ef4444" : "#e2e8f0",
                boxShadow: invoiceError ? "0 0 0 3px rgba(239,68,68,0.1)" : "none",
              }}
              onFocus={(e) => { if (!invoiceError) e.target.style.borderColor = "#6366f1"; }}
              onBlur={(e) => { if (!invoiceError) e.target.style.borderColor = "#e2e8f0"; }}
              onKeyDown={(e) => e.key === "Enter" && handleInvoiceNext()}
            />
            {invoiceError && (
              <p className="flex items-center gap-1 text-[10px] text-red-500 mt-1.5">
                <AlertCircle size={10} /> {invoiceError}
              </p>
            )}

            <button
              onClick={handleInvoiceNext}
              className="w-full mt-4 py-2.5 rounded-xl text-[12px] font-bold text-white transition-all hover:opacity-90 active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)", boxShadow: "0 2px 10px rgba(99,102,241,0.35)" }}
            >
              Continue to Send OTP →
            </button>
          </div>
        )}

        {/* ── Send OTP Step ── */}
        {step === "sendOtp" && (
          <div className="p-7">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-[15px] font-black text-slate-900">Send OTP to Email</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Invoice: <span className="text-indigo-600 font-bold">{invoiceVal}</span>
                </p>
              </div>
              <button onClick={onClose} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
                <X size={13} />
              </button>
            </div>

            <StepIndicator steps={STEPS} current={1} />

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-[10px] space-y-1.5">
              {[
                ["Registered Email", "r***l@goelectronix.in"],
                ["OTP Valid For",    "5 minutes"],
              ].map(([lbl, val]) => (
                <div key={lbl} className="flex justify-between">
                  <span className="text-slate-500">{lbl}</span>
                  <span className="font-bold text-slate-800">{val}</span>
                </div>
              ))}
            </div>

            {isSending ? (
              <div className="flex items-center justify-center gap-3 py-4 text-[12px] font-semibold text-indigo-600">
                <div
                  className="w-4 h-4 rounded-full border-2 border-indigo-200"
                  style={{ borderTopColor: "#6366f1", animation: "spin 0.7s linear infinite" }}
                />
                Sending OTP to your email...
              </div>
            ) : (
              <>
                <p className="text-[11px] text-slate-500 leading-relaxed mb-4">
                  An OTP will be sent to your registered email address to verify this payout summary.
                </p>
                <button
                  onClick={handleSendOtp}
                  className="w-full py-2.5 rounded-xl text-[12px] font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
                  style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)", boxShadow: "0 2px 10px rgba(99,102,241,0.35)" }}
                >
                  <Mail size={13} /> Send OTP via Email
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Verify OTP Step ── */}
        {step === "verifyOtp" && (
          <div className="p-7">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-[15px] font-black text-slate-900">Enter Verification OTP</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Sent to r***l@goelectronix.in</p>
              </div>
              <button onClick={onClose} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
                <X size={13} />
              </button>
            </div>

            <StepIndicator steps={STEPS} current={2} />

            <p className="text-[11px] text-slate-500 mb-1 leading-relaxed">
              Enter the 6-digit OTP sent to your email.{" "}
              <span className="text-green-600 font-semibold">✓ OTP sent successfully.</span>
            </p>

            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 mt-4">
              6-Digit OTP
            </label>
            <div className="flex gap-2 mb-1">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={otpRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpInput(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeydown(e, i)}
                  className="w-11 h-12 text-center rounded-xl text-[18px] font-black font-mono outline-none border-2 transition-all"
                  style={{
                    borderColor: otpError ? "#ef4444" : digit ? "#6366f1" : "#e2e8f0",
                    color: "#1e293b",
                    boxShadow: digit && !otpError ? "0 0 0 3px rgba(99,102,241,0.1)" : "none",
                  }}
                />
              ))}
            </div>
            {otpError && (
              <p className="flex items-center gap-1 text-[10px] text-red-500 mb-2">
                <AlertCircle size={10} /> {otpError}
              </p>
            )}

            <button
              onClick={handleVerifyOtp}
              className="w-full mt-4 py-2.5 rounded-xl text-[12px] font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)", boxShadow: "0 2px 10px rgba(99,102,241,0.35)" }}
            >
              <ShieldCheck size={13} /> Verify & Confirm
            </button>

            <p className="text-center text-[10px] text-slate-400 mt-3">
              Didn't receive it?{" "}
              <button onClick={resendOtp} className="text-indigo-600 font-bold hover:underline">
                Resend OTP
              </button>
            </p>
          </div>
        )}

        {/* ── Success Step ── */}
        {step === "success" && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl" style={{ background: "#dcfce7" }}>
              ✅
            </div>
            <h3 className="text-[17px] font-black text-slate-900 mb-2">Summary Verified!</h3>
            <p className="text-[11px] text-slate-500 leading-relaxed mb-6">
              Payout summary for <strong className="text-slate-700">Week 42–43 · 2025</strong> has been
              successfully verified and marked as billed. Redirecting you to the Billed tab.
            </p>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${redirectPct}%`, background: "linear-gradient(90deg,#4f46e5,#6366f1)" }}
              />
            </div>
            <p className="text-[10px] text-slate-400">Redirecting in {redirectSecs}s...</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}